import { Logo } from '../../components/common/Logo';
import { cn } from '../../lib/utils';

/**
 * DiscovrLogo — the Discovr AI logo lockup (Figma footer/header logo).
 *
 * Composes the existing brand mark with the "Discovr" wordmark (DM Sans) into
 * one consistent, theme-driven component, replacing the ad-hoc `<Logo/> +
 * <span>Discovr</span>` repetitions across the apps. Colour follows
 * `currentColor` by default, so it inherits the surrounding text colour (pass
 * `color` to override). Use `markOnly` for the square mark / favicon.
 */
interface DiscovrLogoProps {
  /** Mark height in px; the wordmark scales with it. Default 24. */
  size?: number;
  /** Render only the square mark (no wordmark). Default false. */
  markOnly?: boolean;
  /** Fill colour. Defaults to `currentColor` so it themes with text colour. */
  color?: string;
  className?: string;
  /** Accessible label. Default "Discovr". */
  title?: string;
}

export function DiscovrLogo({
  size = 24,
  markOnly = false,
  color = 'currentColor',
  className,
  title = 'Discovr',
}: DiscovrLogoProps) {
  return (
    <span
      className={cn('inline-flex items-center font-dm-sans', className)}
      style={{ color, gap: size * 0.33 }}
      role="img"
      aria-label={title}
    >
      <Logo size={size} color="currentColor" title={title} />
      {!markOnly && (
        <span
          aria-hidden
          style={{
            fontSize: size * 0.83,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1,
          }}
        >
          Discovr
        </span>
      )}
    </span>
  );
}

export type { DiscovrLogoProps };
